import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, ExternalLink } from "lucide-react";
import notesImg from "@/assets/notes-watercolor.png";
import drumImg from "@/assets/drum-watercolor.png";
import webinarIcon from "@/assets/brand-icon-webinar.png";
import akademieIcon from "@/assets/brand-icon-akademie.png";
import ausbildungIcon from "@/assets/brand-icon-ausbildung.png";
import mikoIcon from "@/assets/miko-character.png";
import shopIcon from "@/assets/brand-icon-shop.png";
import kursleiterIcon from "@/assets/brand-icon-kursleiter.png";
import webinarHero from "@/assets/hero-webinar.jpg";
import akademieHero from "@/assets/hero-akademie.jpg";
import ausbildungHero from "@/assets/hero-ausbildung.jpg";
import mikoHero from "@/assets/hero-miko.jpg";
import shopHero from "@/assets/hero-shop.jpg";
import kursleiterHero from "@/assets/hero-kursleiter.jpg";
import { KURSE_ZAHL, LEKTIONEN_TEXT, LEKTIONEN_KURZ, gross } from "@/config/webseiteFakten";

const features = [
  {
    icon: webinarIcon,
    heroImage: webinarHero,
    title: "Live-Webinare",
    shortDesc: "Frische Impulse und Austausch in Echtzeit",
    fullDesc: "Nimm an interaktiven Live-Sessions teil und tausche dich mit Expert:innen und Kolleg:innen aus. Erhalte praktische Tipps, die du sofort umsetzen kannst.",
    color: "from-emerald-500/20 via-teal-500/20 to-emerald-500/20",
    accentColor: "from-emerald-500 to-teal-500",
    link: "/termine",
    external: true,
    cta: "Termine ansehen",
  },
  {
    icon: akademieIcon,
    heroImage: akademieHero,
    title: "Online-Akademie",
    shortDesc: `${LEKTIONEN_KURZ} Lieder, Videos & Materialien`,
    fullDesc: `Entdecke unsere umfangreiche Online-Bibliothek mit über ${KURSE_ZAHL} Kursen und Kreativpaketen. ${gross(LEKTIONEN_TEXT)} mit Videos, Audios, PDFs und Praxismaterialien warten auf dich.`,
    color: "from-blue-500/20 via-teal-500/20 to-blue-500/20",
    accentColor: "from-blue-500 to-teal-500",
    link: "/kurse",
    external: false,
    cta: "Kurse entdecken",
  },
  {
    icon: ausbildungIcon,
    heroImage: ausbildungHero,
    title: "Ausbildungen",
    shortDesc: "Ausbildungen mit Zertifikat",
    // Staatlich zugelassen (ZFU) ist nur Fachkraft für Musik (Sitzung „Aufräumen“, 14.10.2026).
    fullDesc: "Erweitere deine Kompetenzen mit unseren Ausbildungen: Fachkraft für Musik im Elementarbereich (staatlich zugelassener Fernlehrgang, ZFU), Fachkraft für Musik und Sprache und der große Kita-Gitarrenkurs.",
    color: "from-violet-500/20 via-purple-500/20 to-violet-500/20",
    accentColor: "from-violet-500 to-purple-500",
    link: "/ausbildung/fachkraft-musik",
    external: true,
    cta: "Mehr erfahren",
  },
  {
    icon: mikoIcon,
    heroImage: mikoHero,
    title: "Miko-Studio",
    shortDesc: "Dein kreativer KI-Assistent",
    fullDesc: "Miko unterstützt dich bei der Planung deiner Einheiten, erstellt Dokumentationen und gibt dir kreative Ideen für deine musikalischen Aktivitäten.",
    color: "from-indigo-500/20 via-blue-500/20 to-indigo-500/20",
    accentColor: "from-indigo-500 to-blue-500",
    link: "/miko",
    external: false,
    cta: "Miko-Studio kennenlernen",
  },
  {
    icon: shopIcon,
    heroImage: shopHero,
    title: "Shop",
    shortDesc: "Materialien für die Praxis",
    fullDesc: "Hochwertige Instrumente, Liederbücher, Arbeitsblätter und mehr. Alles was du für eine erfolgreiche musikalische Arbeit mit Kindern brauchst.",
    color: "from-orange-500/20 via-red-500/20 to-orange-500/20",
    accentColor: "from-orange-500 to-red-500",
    link: "https://shop.musikonzept.de/",
    external: true,
    cta: "Shop besuchen",
  },
  {
    icon: kursleiterIcon,
    heroImage: kursleiterHero,
    title: "Kursverwaltungs-App",
    shortDesc: "Kursverwaltung & Buchungssystem",
    fullDesc: "Wir entwickeln gerade ein Tool, mit dem du deine Kurse planen, Teilnehmenden verwalten und Buchungen organisieren kannst – alles an einem Ort.",
    color: "from-green-500/20 via-emerald-500/20 to-green-500/20",
    accentColor: "from-green-500 to-emerald-500",
    comingSoon: true,
    cta: "Bald verfügbar",
  },
];

const FeatureCardsSection = () => {
  return (
    <section className="py-24 md:py-32 relative overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-hero-gradient-start via-background to-hero-gradient-end opacity-60" />
      
      {/* Floating Decorative Elements */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] opacity-[0.07] animate-float pointer-events-none">
        <img src={notesImg} alt="" className="w-full h-full object-contain" />
      </div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] opacity-[0.06] animate-float-delayed pointer-events-none">
        <img src={drumImg} alt="" className="w-full h-full object-contain" />
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-24 space-y-6 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground leading-tight">
            Entdecke{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-gradient">
              MusiKonzept
            </span>
          </h2>
          <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Alles, was du für deine musikalische und pädagogische Entwicklung brauchst
          </p>
        </div>

        {/* Asymmetric Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto">
          {features.map((feature, index) => {
            const CardWrapper = feature.link && !feature.comingSoon ? 'a' : 'div';
            const cardProps = feature.link && !feature.comingSoon ? {
              href: feature.link,
              target: feature.external ? "_blank" : undefined,
              rel: feature.external ? "noopener noreferrer" : undefined,
            } : {};

            return (
              <CardWrapper 
                key={index} 
                {...cardProps} 
                className={`block group ${
                  index === 0 || index === 3 ? 'md:col-span-2 lg:col-span-1' : ''
                }`}
              >
                {/* Flip Card Container */}
                <div 
                  className="relative h-[320px] md:h-[360px] lg:h-[400px] perspective-1000"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flip-card-inner w-full h-full transition-all duration-700 group-hover:[transform:rotateY(180deg)]">
                    
                    {/* FRONT SIDE */}
                    <Card className="flip-card-front absolute inset-0 backface-hidden border-0 bg-card/40 backdrop-blur-xl shadow-2xl overflow-hidden">
                      {/* Hero Image */}
                      <div className="absolute inset-0">
                        <img 
                          src={feature.heroImage} 
                          alt={feature.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          style={feature.title === "Miko-Studio" ? { objectPosition: '50% 30%' } : undefined}
                        />
                        {/* Gradient Overlay */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} backdrop-blur-[2px]`} />
                        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
                      </div>

                      {/* Content Overlay */}
                      <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
                        {/* Floating Icon - only for Miko-Studio */}
                        {feature.title === "Miko-Studio" && (
                          <div className="absolute top-6 right-6 w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-background/90 backdrop-blur-md p-3 shadow-xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                            <img 
                              src={feature.icon} 
                              alt=""
                              className="w-full h-full object-contain"
                            />
                          </div>
                        )}

                        {feature.comingSoon && (
                          <Badge className="absolute top-6 left-6 bg-accent/90 backdrop-blur-md text-accent-foreground shadow-lg">
                            Bald verfügbar
                          </Badge>
                        )}

                        <div className="space-y-3">
                          <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                            {feature.title}
                          </h3>
                          <p className="text-base md:text-lg text-muted-foreground">
                            {feature.shortDesc}
                          </p>
                        </div>

                        {!feature.comingSoon && (
                          <div className="mt-4 flex items-center gap-2 text-primary font-semibold">
                            <span>Mehr entdecken</span>
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                          </div>
                        )}
                      </div>
                    </Card>

                    {/* BACK SIDE */}
                    <Card className="flip-card-back absolute inset-0 backface-hidden [transform:rotateY(180deg)] border-0 overflow-hidden bg-card">
                      {/* Subtle Gradient Background */}
                      <div className="absolute inset-0 bg-gradient-to-br from-background via-muted/30 to-background" />
                      
                      {/* Accent Color Bar */}
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary" />

                      {/* Content */}
                      <CardContent className="relative h-full flex flex-col justify-between p-6 md:p-8">
                        <div className="space-y-6">
                          {/* Icon */}
                          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary via-accent to-primary p-0.5 shadow-xl">
                            <div className="w-full h-full rounded-2xl bg-background p-3">
                              <img 
                                src={feature.icon} 
                                alt=""
                                className="w-full h-full object-contain"
                              />
                            </div>
                          </div>

                          {/* Title */}
                          <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                            {feature.title}
                          </h3>

                          {/* Full Description */}
                          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                            {feature.fullDesc}
                          </p>
                        </div>

                        {/* CTA Button */}
                        <Button 
                          size="lg"
                          className="w-full bg-gradient-to-r from-primary via-accent to-primary text-white hover:shadow-xl hover:scale-[1.02] transition-all duration-300 shadow-md"
                          disabled={feature.comingSoon}
                        >
                          <span>{feature.cta}</span>
                          {feature.comingSoon ? (
                            <span className="ml-2">⏱️</span>
                          ) : feature.external ? (
                            <ExternalLink className="w-5 h-5 ml-2" />
                          ) : (
                            <ArrowRight className="w-5 h-5 ml-2" />
                          )}
                        </Button>
                      </CardContent>
                    </Card>

                  </div>
                </div>
              </CardWrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeatureCardsSection;
